/**
 * Oil & Gas — OGI Survey Mission Planner  (Step 5 of the 8-step intelligence loop)
 *
 * Plan a low-altitude OGI (Optical Gas Imaging) or thermal drone survey mission
 * over a confirmed anomaly corridor. Outputs: payload selection, altitude, wind
 * window constraints, and a pre-flight briefing for the operator.
 */
import React, { useState, useEffect } from 'react';
import { Route, Wind, Thermometer, FlameKindling, AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Edit3, RotateCcw } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { anomalyApi } from '../../services/api';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PipelineAnomaly {
  id: string;
  boundary_name: string;
  anomaly_type: string;
  severity: string;
  affected_area_hectares?: number | string;
  description?: string;
  detection_date: string;
}

interface SurveyPlan {
  primaryPayload:     string;
  altitudeM:          number;
  speedMps:           number;
  overlapPercent:     number;
  estimatedFlightMin: number;
  windLimitMps:       number;
  weatherConstraints: string[];
  safetyNotes:        string[];
}

// Fields that can be overridden by the operator
type PlanOverrides = Pick<SurveyPlan, 'primaryPayload' | 'altitudeM' | 'speedMps' | 'overlapPercent' | 'estimatedFlightMin' | 'windLimitMps'>;

// ─── Derive a survey plan from anomaly type ───────────────────────────────────

function derivePlan(anomaly: PipelineAnomaly): SurveyPlan {
  const isLeak   = /leak|methane|gas|die.off|vegetation/i.test(anomaly.anomaly_type);
  const isThermal = /thermal|hot|flare/i.test(anomaly.anomaly_type);

  return {
    primaryPayload:     isLeak ? 'FLIR GF320 OGI Camera' : isThermal ? 'FLIR Vue Pro R Thermal' : 'FLIR GF320 OGI Camera',
    altitudeM:          isLeak ? 30 : 50,
    speedMps:           isLeak ? 3 : 5,
    overlapPercent:     80,
    estimatedFlightMin: Math.max(15, Math.round((Number(anomaly.affected_area_hectares) || 5) * 4)),
    windLimitMps:       isLeak ? 3 : 5,
    weatherConstraints: [
      `Max wind speed: ${isLeak ? '3 m/s' : '5 m/s'} at survey altitude`,
      'Avoid thermals — fly early morning or late afternoon',
      'Cloud cover < 50% (avoid rain)',
      isLeak ? 'OGI requires controlled illumination — avoid direct sunlight glare' : 'Thermal most effective within 2 hr of sunrise/sunset',
    ],
    safetyNotes: [
      'Notify pipeline control room before flight',
      'Maintain 30 m lateral clearance from pipeline infrastructure',
      'Carry gas detector — do not fly directly over suspected release point',
      'Coordinate with surface teams — no entry to affected zone during flight',
    ],
  };
}

// ─── Badge helpers ────────────────────────────────────────────────────────────

function SevBadge({ sev }: { sev: string }) {
  const cls: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    high:     'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    medium:   'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    low:      'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${cls[sev] ?? cls.low}`}>{sev}</span>;
}

function PlanRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white text-right max-w-xs">{value}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const OgiSurveyPlannerPage: React.FC = () => {
  const [anomalies, setAnomalies]       = useState<PipelineAnomaly[]>([]);
  const [selectedId, setSelectedId]     = useState('');
  const [loading, setLoading]           = useState(true);
  const [creating, setCreating]         = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [success, setSuccess]           = useState<string | null>(null);
  const [missionName, setMissionName]   = useState('');
  const [notes, setNotes]               = useState('');
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrides, setOverrides]       = useState<PlanOverrides | null>(null);
  const [defaults, setDefaults]         = useState<PlanOverrides | null>(null);

  useEffect(() => {
    anomalyApi.getAnomalies({ status: 'investigating', vertical: 'oil_gas' } as any)
      .then((res) => setAnomalies(res.anomalies ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selected = anomalies.find((a) => a.id === selectedId) ?? null;

  useEffect(() => {
    if (selected) {
      setMissionName(`OGI Survey — ${selected.boundary_name} (${selected.anomaly_type.replace(/_/g, ' ')})`);
      const p = derivePlan(selected);
      const d: PlanOverrides = { primaryPayload: p.primaryPayload, altitudeM: p.altitudeM, speedMps: p.speedMps, overlapPercent: p.overlapPercent, estimatedFlightMin: p.estimatedFlightMin, windLimitMps: p.windLimitMps };
      setDefaults(d);
      setOverrides(d);
      setOverrideOpen(false);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const aiPlan = selected ? derivePlan(selected) : null;
  const plan   = aiPlan && overrides ? { ...aiPlan, ...overrides } : aiPlan;

  const setOv = <K extends keyof PlanOverrides>(k: K, v: number) =>
    setOverrides((p) => p ? { ...p, [k]: v } : p);

  const overriddenKeys = overrides && defaults
    ? (Object.keys(overrides) as (keyof PlanOverrides)[]).filter((k) => overrides[k] !== defaults[k])
    : [];

  const setOvStr = (k: 'primaryPayload', v: string) =>
    setOverrides((p) => p ? { ...p, [k]: v } : p);
  const hasOverrides = overriddenKeys.length > 0;

  const handleCreateMission = async () => {
    if (!selected || !missionName.trim()) { setError('Provide a mission name'); return; }
    setCreating(true); setError(null);
    try {
      await api.post('/missions', {
        name:         missionName.trim(),
        description:  [
          notes.trim() || `OGI survey for anomaly on ${selected.boundary_name}`,
          hasOverrides ? `Operator overrides applied (${overriddenKeys.length}): ${overriddenKeys.join(', ')}` : 'AI defaults used.',
        ].filter(Boolean).join('\n'),
        mission_type: 'ogi_survey',
        metadata: {
          anomaly_id:        selected.id,
          corridor_name:     selected.boundary_name,
          payload:           plan?.primaryPayload,
          altitude_m:        plan?.altitudeM,
          speed_mps:         plan?.speedMps,
          overlap_pct:       plan?.overlapPercent,
          wind_limit_mps:    plan?.windLimitMps,
          operator_overrides: hasOverrides ? overriddenKeys : [],
          vertical:          'oil_gas',
          step:              5,
        },
      });
      setSuccess(`Mission "${missionName}" created${hasOverrides ? ` (${overriddenKeys.length} operator override${overriddenKeys.length > 1 ? 's' : ''} recorded)` : ''} — proceed to Flight Data & Analysis`);
      setMissionName('');
      setNotes('');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to create mission');
    } finally {
      setCreating(false);
    }
  };

  const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Route className="w-7 h-7 text-orange-500" /> OGI Survey Mission Planner
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Step 5 — Plan low-altitude OGI/thermal drone survey over a confirmed pipeline anomaly
        </p>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Anomaly selection ── */}
      <Card>
        <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Select Confirmed Anomaly</h2>
        {loading ? (
          <div className="flex justify-center py-6"><Spinner size="md" /></div>
        ) : anomalies.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No confirmed anomalies awaiting OGI survey</p>
            <p className="text-xs mt-1">Confirm anomalies in <strong>Anomaly Alerts → Step 4</strong> first</p>
          </div>
        ) : (
          <div className="space-y-2">
            {anomalies.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelectedId(a.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 text-left transition-all ${selectedId === a.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{a.boundary_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{a.anomaly_type?.replace(/_/g, ' ')}</p>
                </div>
                <SevBadge sev={a.severity} />
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* ── Survey plan ── */}
      {plan && selected && overrides && defaults && (
        <>
          <Card>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <FlameKindling className="w-5 h-5 text-orange-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200">AI-Derived Survey Plan</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">OGI parameters from anomaly type · gas concentration risk · regulatory clearance</p>
              </div>
              {hasOverrides && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-semibold">
                  <Edit3 className="w-3 h-3" /> {overriddenKeys.length} override{overriddenKeys.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <PlanRow label="Primary Payload"  value={<span className={overrides.primaryPayload !== defaults.primaryPayload ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.primaryPayload}{overrides.primaryPayload !== defaults.primaryPayload ? ' ✱' : ''}</span>} />
            <PlanRow label="Survey Altitude"  value={<span className={overrides.altitudeM !== defaults.altitudeM ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.altitudeM} m AGL{overrides.altitudeM !== defaults.altitudeM ? ' ✱' : ''}</span>} />
            <PlanRow label="Cruise Speed"     value={<span className={overrides.speedMps !== defaults.speedMps ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.speedMps} m/s{overrides.speedMps !== defaults.speedMps ? ' ✱' : ''}</span>} />
            <PlanRow label="Side Overlap"     value={<span className={overrides.overlapPercent !== defaults.overlapPercent ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.overlapPercent}%{overrides.overlapPercent !== defaults.overlapPercent ? ' ✱' : ''}</span>} />
            <PlanRow label="Est. Flight Time" value={<span className={overrides.estimatedFlightMin !== defaults.estimatedFlightMin ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.estimatedFlightMin} min{overrides.estimatedFlightMin !== defaults.estimatedFlightMin ? ' ✱' : ''}</span>} />
            <PlanRow label="Max Wind Speed"   value={<span className={overrides.windLimitMps !== defaults.windLimitMps ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{plan.windLimitMps} m/s{overrides.windLimitMps !== defaults.windLimitMps ? ' ✱' : ''}</span>} />

            {/* Override accordion */}
            <button
              type="button"
              onClick={() => setOverrideOpen((v) => !v)}
              className="mt-5 w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-dashed border-orange-300 dark:border-orange-700 bg-orange-50/50 dark:bg-orange-950/10 hover:bg-orange-50 dark:hover:bg-orange-950/20 transition-colors text-sm font-semibold text-orange-700 dark:text-orange-400"
            >
              <span className="flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                {overrideOpen ? 'Hide' : 'Override'} Flight Parameters
                {hasOverrides && !overrideOpen && <span className="font-normal text-orange-600 dark:text-orange-500">({overriddenKeys.length} modified)</span>}
              </span>
              {overrideOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {overrideOpen && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Amber fields have been modified from AI defaults.</p>
                  {hasOverrides && (
                    <button type="button" onClick={() => setOverrides({ ...defaults })} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                      <RotateCcw className="w-3 h-3" /> Reset to AI defaults
                    </button>
                  )}
                </div>

                {/* Payload (text) */}
                {(() => {
                  const changed = overrides.primaryPayload !== defaults.primaryPayload;
                  return (
                    <div className={`p-3 rounded-lg border transition-colors ${changed ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[11px] font-semibold uppercase tracking-wider ${changed ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>Primary Payload{changed ? ' · overridden' : ''}</span>
                        <span className="text-[11px] text-gray-400">AI: {defaults.primaryPayload}</span>
                      </div>
                      <input
                        type="text"
                        value={overrides.primaryPayload}
                        onChange={(e) => setOvStr('primaryPayload', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  );
                })()}

                {/* Numeric params */}
                {([
                  { key: 'altitudeM' as const, label: 'Survey Altitude', unit: 'm AGL', min: 15, max: 120, step: 5, hint: `AI: ${defaults.altitudeM} m` },
                  { key: 'speedMps'  as const, label: 'Cruise Speed',    unit: 'm/s',  min: 1,  max: 15,  step: 0.5, hint: `AI: ${defaults.speedMps} m/s` },
                  { key: 'overlapPercent' as const, label: 'Side Overlap', unit: '%', min: 60, max: 95, step: 5, hint: `AI: ${defaults.overlapPercent}%` },
                  { key: 'estimatedFlightMin' as const, label: 'Est. Flight Time', unit: 'min', min: 5, max: 240, step: 5, hint: `AI: ${defaults.estimatedFlightMin} min` },
                  { key: 'windLimitMps' as const, label: 'Max Wind Limit', unit: 'm/s', min: 1, max: 12, step: 0.5, hint: `AI: ${defaults.windLimitMps} m/s` },
                ] as const).map(({ key, label, unit, min, max, step, hint }) => {
                  const changed = overrides[key] !== defaults[key];
                  return (
                    <div key={key} className={`p-3 rounded-lg border transition-colors ${changed ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[11px] font-semibold uppercase tracking-wider ${changed ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>{label}{changed ? ' · overridden' : ''}</span>
                        <span className="text-[11px] text-gray-400">{hint}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="number" min={min} max={max} step={step} value={overrides[key]}
                          onChange={(e) => setOv(key, Number(e.target.value))}
                          className="w-24 px-2 py-1.5 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">{unit}</span>
                        <input type="range" min={min} max={max} step={step} value={overrides[key]}
                          onChange={(e) => setOv(key, Number(e.target.value))} className="flex-1 accent-orange-500" />
                      </div>
                    </div>
                  );
                })}
                {overrides.altitudeM < 20 && (
                  <div className="p-2.5 bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-700 rounded text-xs text-red-700 dark:text-red-300">
                    <strong>Safety:</strong> Altitude below 20 m AGL over a live pipeline release zone is below minimum safe separation. Raise to at least 20 m or obtain safety exemption.
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 space-y-3">
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-3">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5 flex items-center gap-1">
                  <Wind className="w-3.5 h-3.5" /> Weather Constraints
                </p>
                <ul className="space-y-0.5">
                  {plan.weatherConstraints.map((c, i) => (
                    <li key={i} className="text-xs text-amber-700 dark:text-amber-300">• {c}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 p-3">
                <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1.5 flex items-center gap-1">
                  <Thermometer className="w-3.5 h-3.5" /> Safety Notes
                </p>
                <ul className="space-y-0.5">
                  {plan.safetyNotes.map((n, i) => (
                    <li key={i} className="text-xs text-red-700 dark:text-red-300">• {n}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>

          {/* ── Create mission ── */}
          <Card>
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Create Survey Mission</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mission Name *</label>
                <input className={INPUT_CLS}
                  value={missionName} onChange={(e) => setMissionName(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pilot Notes (optional)</label>
                <textarea rows={2} className={INPUT_CLS} placeholder="Wind conditions, access notes, equipment checks…"
                  value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="mt-4">
              <Button onClick={handleCreateMission} loading={creating} disabled={!missionName.trim()}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Create Mission{hasOverrides ? ' (with overrides)' : ''}
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default OgiSurveyPlannerPage;

// Made with Bob
